import { useEffect, useState } from "react";
import {
  createSupplier,
  listSuppliers,
  updateSupplier,
  type AdminSession,
  type SupplierItem,
} from "@/lib/api";
import Card from "../components/Card";

type SupplierForm = {
  name: string;
  rut: string;
  phone: string;
  active: boolean;
};

const emptyForm: SupplierForm = { name: "", rut: "", phone: "", active: true };

export default function ProveedoresSection({ session }: { session: AdminSession }) {
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState<SupplierForm>(emptyForm);

  async function load(currentSession: AdminSession): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const data = await listSuppliers(currentSession);
      setSuppliers(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar proveedores");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(session);
  }, [session]);

  function startEdit(supplier: SupplierItem): void {
    setEditingId(supplier.id);
    setForm({ name: supplier.name, rut: supplier.rut, phone: supplier.phone, active: supplier.active });
  }

  function cancelEdit(): void {
    setEditingId("");
    setForm(emptyForm);
  }

  async function handleSubmit(): Promise<void> {
    if (!form.rut.trim() || !form.name.trim()) {
      setError("Completa razón social y RUT del proveedor.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await updateSupplier(session, editingId, {
          name: form.name.trim(),
          rut: form.rut.trim(),
          phone: form.phone.trim() || undefined,
          active: form.active,
        });
      } else {
        await createSupplier(session, {
          name: form.name.trim(),
          rut: form.rut.trim(),
          phone: form.phone.trim() || undefined,
          active: form.active,
        });
      }
      cancelEdit();
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar el proveedor");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {error ? (
        <div className="lg:col-span-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="lg:col-span-1">
        <Card title={editingId ? "Editar Proveedor" : "Nuevo Proveedor"}>
          <div className="space-y-2">
            <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Razón social" />
            <input value={form.rut} onChange={(e) => setForm((p) => ({ ...p, rut: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="RUT" />
            <input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="Teléfono" />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
              Proveedor activo
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            {editingId ? (
              <button onClick={cancelEdit} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">Cancelar</button>
            ) : null}
            <button disabled={saving} onClick={() => void handleSubmit()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">
              {editingId ? "Guardar cambios" : "Crear proveedor"}
            </button>
          </div>
        </Card>
      </div>

      <div className="lg:col-span-2">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Proveedores</h2>
            <span className="text-xs text-gray-500">{suppliers.length} registros</span>
          </div>
          {loading ? (
            <div className="text-sm text-gray-500">Cargando proveedores...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase text-gray-500">
                    <th className="px-3 py-2">Razón social</th>
                    <th className="px-3 py-2">RUT</th>
                    <th className="px-3 py-2">Teléfono</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((supplier) => (
                    <tr key={supplier.id} className="border-b border-gray-50 text-sm text-gray-700">
                      <td className="px-3 py-2">{supplier.name}</td>
                      <td className="px-3 py-2">{supplier.rut}</td>
                      <td className="px-3 py-2">{supplier.phone || "-"}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-1 text-xs ${supplier.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-700"}`}>
                          {supplier.active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => startEdit(supplier)} className="rounded-lg border border-blue-200 px-2 py-1 text-xs text-blue-700">Editar</button>
                      </td>
                    </tr>
                  ))}
                  {suppliers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-sm text-gray-500">No hay proveedores registrados.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
